import {
  CustomTooltip,
  Loader,
  Modal,
  ModalContent,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { Grid, Typography } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import {
  ArrowsExpandOutlineIcon,
  DocumentTextOutlineIcon,
  Icon,
  MinusOutlineIcon,
  PlusOutlineIcon,
  RotateOutlineIcon,
  XOutlineIcon,
} from '@fountain/ripple/icon';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { classNames } from 'react-extras';
import { useIntl } from 'react-intl';
import useScrollOnDrag from 'react-scroll-ondrag';
import { v4 as uuid } from 'uuid';

import useBlobRotate from 'hooks/useBlobRotate';
import { rippleModalHeaderIcon } from 'components/ModalHeaderIcon/rippleModalHeaderIcon';
import { extensionFinder } from 'utils/extensionFinder';
import { numberFormatting } from 'utils/formatNumber';
import { truncateFileName } from 'utils/stringutils';

import useStyles from '../styles';
import messages from './messages';
import PdfViewer from './PDFViewer';

const DEFAULT_SCALE = 1;
const MAX_SCALE = 5;
const MIN_SCALE = 0.2;
const ROTATION_ANGLE = 90;
const MAX_ROTATION = 360;
const DocumentHeaderIcon = rippleModalHeaderIcon(DocumentTextOutlineIcon);

export const DocumentFilePreview = ({
  applicantId,
  initialImageSrc,
  filename,
  id,
  requestedReupload,
  previewUrl,
  onOpenFullPage,
}) => {
  const { handleRotateSave, isLoading: isLoadingBlob } =
    useBlobRotate(applicantId);
  const filePreviewRef = useRef();
  const { events } = useScrollOnDrag(filePreviewRef);
  const { formatMessage } = useIntl();

  const [rotate, setRotate] = useState(0);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);

  const classes = useStyles({ rotate, scale });

  const handleDocumentModalClose = () => setIsDocumentModalOpen(false);

  const onRotate = targetDegree => {
    handleRotateSave(id, targetDegree);
    if (Math.abs(rotate + targetDegree) === MAX_ROTATION) return setRotate(0);
    return setRotate(rotate + targetDegree);
  };

  const onZoomInClick = () => {
    const incrementValue = scale < DEFAULT_SCALE ? 0.2 : DEFAULT_SCALE;
    const newScale = numberFormatting(scale + incrementValue, 1);
    if (newScale > MAX_SCALE) return setScale(MAX_SCALE);
    return setScale(newScale);
  };

  const onZoomOutClick = () => {
    const decrementValue = scale <= DEFAULT_SCALE ? 0.2 : DEFAULT_SCALE;
    const newScale = numberFormatting(scale - decrementValue, 1);
    if (newScale < MIN_SCALE) return setScale(MIN_SCALE);
    return setScale(newScale);
  };

  const onFullscreenClick = () => {
    setIsFullscreen(!isFullscreen);
  };

  const onDocumentPreview = () => {
    setIsDocumentModalOpen(!isDocumentModalOpen);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
  };

  const fullscreenLabel = formatMessage(
    onOpenFullPage ? messages.openFullPage : messages.fullScreen,
  );
  const handleOpenFullPage = event => {
    event.stopPropagation();
    onOpenFullPage();
  };

  const renderHoverActions = () => {
    if (requestedReupload) return null;
    return (
      <Grid
        container
        className={classes.previewHoverActions}
        alignItems="center"
        justify="flex-end"
      >
        <Grid item container direction="row" justify="flex-end">
          <CustomTooltip title={fullscreenLabel} dense>
            <ButtonBase
              aria-label={fullscreenLabel}
              onClick={onOpenFullPage ? handleOpenFullPage : onFullscreenClick}
              className={classNames(classes.previewHoverAction, {
                fullScreen: isFullscreen,
              })}
            >
              <Icon icon={ArrowsExpandOutlineIcon} size="sm" />
            </ButtonBase>
          </CustomTooltip>
          <CustomTooltip title={formatMessage(messages.rotateRight)} dense>
            <ButtonBase
              aria-label={formatMessage(messages.rotateRight)}
              onClick={() => onRotate(ROTATION_ANGLE)}
              className={classes.previewHoverAction}
            >
              <Icon
                icon={RotateOutlineIcon}
                size="sm"
                className={classes.mirrored}
              />
            </ButtonBase>
          </CustomTooltip>
          <CustomTooltip title={formatMessage(messages.rotateLeft)} dense>
            <ButtonBase
              aria-label={formatMessage(messages.rotateLeft)}
              onClick={() => onRotate(-ROTATION_ANGLE)}
              className={classNames(classes.previewHoverAction, {
                rotateCounterClockwise: true,
              })}
            >
              <Icon icon={RotateOutlineIcon} size="sm" />
            </ButtonBase>
          </CustomTooltip>
          <CustomTooltip title={formatMessage(messages.zoomIn)} dense>
            {/* cannot supply disabled element to tooltip, hence <span> */}
            <span>
              <ButtonBase
                aria-label={formatMessage(messages.zoomIn)}
                onClick={onZoomInClick}
                classes={{ disabled: classes.previewHoverActionDisabled }}
                className={classes.previewHoverAction}
                disabled={scale === MAX_SCALE}
              >
                <Icon icon={PlusOutlineIcon} size="sm" />
              </ButtonBase>
            </span>
          </CustomTooltip>
          <CustomTooltip title={formatMessage(messages.zoomOut)} dense>
            {/* cannot supply disabled element to tooltip, hence <span> */}
            <span>
              <ButtonBase
                aria-label={formatMessage(messages.zoomOut)}
                onClick={onZoomOutClick}
                classes={{ disabled: classes.previewHoverActionDisabled }}
                className={classNames(classes.previewHoverAction, {
                  zoomOut: true,
                })}
                disabled={scale === MIN_SCALE}
              >
                <Icon icon={MinusOutlineIcon} size="sm" />
              </ButtonBase>
            </span>
          </CustomTooltip>
        </Grid>
      </Grid>
    );
  };

  const renderDocumentHoverActions = () => {
    if (requestedReupload) return null;
    return (
      <Grid
        container
        className={classes.previewHoverActions}
        alignItems="center"
        justify="space-between"
        onClick={onDocumentPreview}
      >
        <Grid item container direction="row" justify="flex-end">
          <CustomTooltip title={fullscreenLabel} dense>
            <ButtonBase
              aria-label={fullscreenLabel}
              onClick={onOpenFullPage ? handleOpenFullPage : onDocumentPreview}
              className={classNames(classes.previewHoverAction, {
                fullScreen: isFullscreen,
              })}
            >
              <Icon icon={ArrowsExpandOutlineIcon} size="sm" />
            </ButtonBase>
          </CustomTooltip>
        </Grid>
      </Grid>
    );
  };
  const fileExtension = extensionFinder(filename);
  if (
    initialImageSrc &&
    ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension)
  ) {
    return (
      <Grid
        container
        className={classNames(
          classes.previewContainer,
          requestedReupload && classes.opaque,
          {
            isFullscreen,
          },
        )}
        data-testid="Document-DocumentFilePreview-ImagePreview"
        ref={filePreviewRef}
        {...events}
      >
        {isFullscreen && (
          <Icon
            icon={XOutlineIcon}
            size="sm"
            className={classes.closeFullscreen}
            onClick={handleCloseFullscreen}
            role="presentation"
          />
        )}
        <Grid
          container
          direction="row"
          alignItems="center"
          justify="center"
          className={classes.previewContainerContent}
          onClick={onFullscreenClick}
        >
          {isLoadingBlob ? (
            <Loader fullSection size="1.5rem" />
          ) : (
            <img
              alt="file preview"
              className={classNames(classes.filePreview, {
                inactive: requestedReupload,
                adjusted: rotate !== 0 || scale !== 0,
              })}
              src={initialImageSrc}
            />
          )}
        </Grid>
        {renderHoverActions()}
      </Grid>
    );
  }

  if (previewUrl && ['pdf'].includes(fileExtension)) {
    return (
      <Grid
        container
        className={classNames(
          classes.previewContainer,
          requestedReupload && classes.opaque,
          {
            isFullscreen,
          },
        )}
        data-testid="Document-DocumentFilePreview-PdfPreview"
        ref={filePreviewRef}
        {...events}
      >
        <Grid
          container
          direction="row"
          alignItems="center"
          justify="center"
          className={classes.previewContainerContent}
          onClick={onDocumentPreview}
        >
          {isLoadingBlob ? (
            <Loader fullSection size="1.5rem" />
          ) : (
            <PdfViewer
              pdfBlob={previewUrl}
              isPreview
              containerId={`pdf-viewer-${uuid()}`}
            />
          )}
          {isDocumentModalOpen && (
            <Modal
              ariaLabelledBy="move-applicant-modal"
              open={isDocumentModalOpen}
              onClose={handleDocumentModalClose}
              fullScreen
              className={classes.pdfViewerContent}
            >
              <ModalHeader
                Icon={DocumentHeaderIcon}
                onClose={handleDocumentModalClose}
              />
              <ModalContent>
                <PdfViewer
                  pdfBlob={previewUrl}
                  containerId={`pdf-viewer-${uuid()}`}
                />
              </ModalContent>
            </Modal>
          )}
        </Grid>
        {renderDocumentHoverActions()}
      </Grid>
    );
  }

  return (
    <Typography
      data-testid="Document-DocumentFilePreview-FileName"
      className={classes.noPreview}
      variant="body2"
    >
      {/* todo: handle case where filename does not have extension; currently returns `filename.filename` */}
      {truncateFileName(filename)}
    </Typography>
  );
};

DocumentFilePreview.propTypes = {
  applicantId: PropTypes.string.isRequired,
  initialImageSrc: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  requestedReupload: PropTypes.bool,
  previewUrl: PropTypes.string,
  onOpenFullPage: PropTypes.func,
};

export default DocumentFilePreview;
